/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { Keys } from '#utils/UNSAFE_keys';
import type { SortCriterionDetail, TableSelectionDetail } from '#UNSAFE_TableView';
import type { TableColumn } from './Table';

import { classNames } from '#utils/UNSAFE_classNames';
import { containsKey } from '#utils/UNSAFE_keys';
import { backgroundStyles, tableHeaderRowStyles } from './themes/Table.css';
import { TableHeaderCell } from './TableHeaderCell';

/**
 * Props for the TableHeader Component
 */
type TableHeaderProps<K, D, C> = {
  tableId: string;
  isRendered: boolean;
  columnsArray: TableColumn<K, D, C>[];
  hasVerticalGridlines: boolean;
  activeKey?: C;
  isTabbableMode: boolean;
  isShowFocusRing: boolean;
  selectedColumnKeys?: Keys<C>;
  selectedRowKeys?: Keys<K>;
  isColumnSelectionEnabled: boolean;
  isRowSelectionEnabled: boolean;
  isResizeHover: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  onSelectionChange?: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void;
  isForceSortIcons: boolean;
  activeStyleKey?: C;
  hoverStyleKey?: C;
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[];
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
  stickyTopOffset?: number;
  startStickyEdge?: number;
  endStickyEdge?: number;
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
  columnBorderEdges: { hasStart: boolean; hasEnd: boolean; width: number }[];
};

/**
 * The internal component used to render a header region in Table.
 */
export function TableHeader<K, D, C>({
  tableId,
  isRendered,
  columnsArray,
  hasVerticalGridlines,
  activeKey,
  isTabbableMode,
  isShowFocusRing,
  selectedColumnKeys,
  selectedRowKeys,
  isColumnSelectionEnabled,
  isRowSelectionEnabled,
  onSelectionChange,
  isForceSortIcons,
  activeStyleKey,
  hoverStyleKey,
  sortCriterion,
  onSortCriterionChange,
  stickyTopOffset,
  startStickyEdge,
  endStickyEdge,
  firstStickyColumnIndex,
  finalStickyColumnIndex,
  columnBorderEdges,
  isResizeHover,
  isRtl,
  isHighContrast
}: TableHeaderProps<K, D, C>) {
  const rowClasses = classNames([tableHeaderRowStyles.base]);

  const getStyle = () => {
    return isResizeHover ? 'cursor: col-resize;' : '';
  };

  let previousColumnKey: C | undefined;
  let previousStickyColumnKey: C | undefined;
  let isPreviousSelectable = false;
  let isPreviousStickySelectable = false;
  let isPreviousColumnSelected = false;
  let isPreviousStickyColumnSelected = false;
  return isRendered ? (
    <thead class={backgroundStyles.inherit} role={'presentation'}>
      <tr
        aria-rowindex={1}
        class={rowClasses}
        role={'row'}
        style={getStyle()}
        data-oj-table-header-row={tableId}>
        {columnsArray.map((column: TableColumn<K, D, C>, columnIndex: number) => {
          const columnKey = column.key;
          let sortDirection;
          if (sortCriterion != null) {
            for (const criterion of sortCriterion) {
              if (criterion.key === columnKey) {
                sortDirection = criterion.sortDirection;
                break;
              }
            }
          }
          const sortable = column.value.sortable;
          const hasSortIcon = sortable === 'enabled' || sortDirection != null;
          const isShowSortIcon =
            hasSortIcon &&
            (isForceSortIcons || sortDirection != null || hoverStyleKey === columnKey);
          const isSticky = column.value.sticky === 'enabled';
          const isSelectable = isColumnSelectionEnabled && column.value.selectable !== 'disabled';
          const isSelected = containsKey(selectedColumnKeys, columnKey);
          const isActive = columnKey === activeKey;
          const hasTabIndex = isActive && !isTabbableMode;
          let previousVisibleColumnKey;
          let isPreviousVisibleColumnSelectable = false;
          let isPreviousVisibleColumnSelected = false;
          let nextVisibleColumn;
          let isNextVisibleColumnSelected = false;
          let isFirstLogicalColumnIndex = false;
          let isFinalLogicalColumnIndex = false;
          if (isSticky) {
            if (startStickyEdge != null && firstStickyColumnIndex === columnIndex) {
              isFirstLogicalColumnIndex = true;
            }
            if (endStickyEdge != null && finalStickyColumnIndex === columnIndex) {
              isFinalLogicalColumnIndex = true;
            }
            if (
              (startStickyEdge != null && startStickyEdge > columnIndex) ||
              (endStickyEdge != null && endStickyEdge <= columnIndex)
            ) {
              for (let i = columnIndex + 1; i < columnsArray.length; i++) {
                if (columnsArray[i].value.sticky === 'enabled') {
                  nextVisibleColumn = columnsArray[i];
                  break;
                }
              }
            } else {
              nextVisibleColumn =
                columnIndex < columnsArray.length - 1 ? columnsArray[columnIndex + 1] : undefined;
            }
            isNextVisibleColumnSelected =
              startStickyEdge === columnIndex
                ? false
                : nextVisibleColumn != null &&
                  containsKey(selectedColumnKeys, nextVisibleColumn.key);
            if (
              (startStickyEdge != null && startStickyEdge >= columnIndex) ||
              (endStickyEdge != null && endStickyEdge < columnIndex)
            ) {
              previousVisibleColumnKey = previousStickyColumnKey;
              isPreviousVisibleColumnSelectable = isPreviousStickySelectable;
              isPreviousVisibleColumnSelected = isPreviousStickyColumnSelected;
            } else if (endStickyEdge !== columnIndex) {
              previousVisibleColumnKey = previousColumnKey;
              isPreviousVisibleColumnSelectable = isPreviousSelectable;
              isPreviousVisibleColumnSelected = isPreviousColumnSelected;
            }
          } else {
            previousVisibleColumnKey = previousColumnKey;
            isPreviousVisibleColumnSelectable = isPreviousSelectable;
            isPreviousVisibleColumnSelected = isPreviousColumnSelected;
          }
          const headerCellProps = {
            tableId: tableId,
            hasVerticalGridlines: hasVerticalGridlines,
            columnKey: columnKey,
            columnIndex: columnIndex,
            isFinalColumnIndex: columnIndex === columnsArray.length - 1,
            isFirstLogicalColumnIndex: isFirstLogicalColumnIndex,
            isFinalLogicalColumnIndex: isFinalLogicalColumnIndex,
            stickyTopOffset: stickyTopOffset,
            isSticky: isSticky,
            isSelected: isSelected,
            isNextColumnSelected: isNextVisibleColumnSelected,
            isPreviousColumnSelected: isPreviousVisibleColumnSelected,
            hasStartEdge: columnBorderEdges[columnIndex].hasStart,
            hasEndEdge: columnBorderEdges[columnIndex].hasEnd,
            selectedRowKeys: selectedRowKeys,
            isColumnSelectable: isSelectable,
            isRowSelectionEnabled: isRowSelectionEnabled,
            isShowFocusRing: isShowFocusRing && isActive,
            hasTabIndex: hasTabIndex,
            isTabbableMode: isTabbableMode,
            headerText: column.value.headerText,
            renderer: column.value.headerRenderer,
            onSelectionChange: onSelectionChange,
            isShowSortIcon: isShowSortIcon,
            sortable: sortable,
            sortDirection: sortDirection,
            onSortCriterionChange: onSortCriterionChange,
            isStickyStartEdge: columnIndex === startStickyEdge,
            isStickyEndEdge: columnIndex === endStickyEdge,
            isActiveStyle: isSelectable && activeStyleKey === columnKey,
            isHoverStyle: isSelectable && hoverStyleKey === columnKey,
            isPreviousCellActiveStyle:
              activeStyleKey != null &&
              activeStyleKey === previousVisibleColumnKey &&
              isPreviousVisibleColumnSelectable,
            isPreviousCellHoverStyle:
              hoverStyleKey != null &&
              hoverStyleKey === previousVisibleColumnKey &&
              isPreviousVisibleColumnSelectable,
            isRtl: isRtl,
            isAccColumnHeader: column.value.accessibleColumnHeader !== 'disabled',
            isHighContrast: isHighContrast,
            padding: column.value.headerPadding,
            tooltip: column.value.headerTooltip,
            focusTarget: column.value.headerFocusTarget,
            horizontalAlignment:
              column.value.headerAlignment?.horizontal != null
                ? column.value.headerAlignment.horizontal
                : column.value.horizontalAlignment,
            verticalAlignment: column.value.headerAlignment?.vertical
          };
          previousColumnKey = columnKey;
          isPreviousSelectable = isSelectable;
          isPreviousColumnSelected = isSelected;
          if (isSticky) {
            previousStickyColumnKey = columnKey;
            isPreviousStickySelectable = isSelectable;
            isPreviousStickyColumnSelected = isSelected;
          }
          return <TableHeaderCell {...headerCellProps} />;
        })}
      </tr>
    </thead>
  ) : null;
}
